import React from 'react';
import { Star, CheckCircle, Shield, BadgeCheck, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BADGE_CONFIG = {
  verified: { icon: CheckCircle, label: 'Documents verified', color: 'text-green-600 bg-green-50' },
  insured: { icon: Shield, label: 'Insurance on file', color: 'text-blue-600 bg-blue-50' },
  dbs_checked: { icon: BadgeCheck, label: 'DBS on file', color: 'text-purple-600 bg-purple-50' },
  right_to_work: { icon: BadgeCheck, label: 'Right to work', color: 'text-teal-600 bg-teal-50' },
};

const ProviderCard = ({ provider, onRequestQuote }) => {
  const navigate = useNavigate();

  const badges = provider.verificationBadges || [];
  const rating = provider.averageRating || 0;
  const reviews = provider.totalReviews || 0;
  const hourlyRate = provider.hourlyRate || provider.pricing?.hourlyRate;

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all overflow-hidden">
      {/* Cover / Image */}
      <div className="h-32 bg-gradient-to-br from-[#1F3A68] to-[#2d5499] relative">
        {provider.coverPhoto && (
          <img src={provider.coverPhoto} alt="" className="w-full h-full object-cover" />
        )}
        {provider.image && (
          <div className="absolute -bottom-6 left-4">
            <img
              src={provider.image}
              alt={provider.businessName || provider.name}
              className="w-14 h-14 rounded-full border-3 border-white bg-white object-cover"
            />
          </div>
        )}
      </div>

      <div className="pt-8 px-4 pb-4">
        {/* Name & Tagline */}
        <h3 className="font-bold text-gray-900 truncate">{provider.businessName || provider.name}</h3>
        {provider.tagline && (
          <p className="text-sm text-gray-500 truncate mt-0.5">{provider.tagline}</p>
        )}

        {/* Rating */}
        <div className="flex items-center gap-1.5 mt-2">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(s => (
              <Star
                key={s}
                size={14}
                className={s <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
              />
            ))}
          </div>
          <span className="text-sm font-semibold text-gray-700">{rating > 0 ? rating.toFixed(1) : '—'}</span>
          <span className="text-xs text-gray-400">({reviews} review{reviews !== 1 ? 's' : ''})</span>
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {badges.slice(0, 3).map(badge => {
              const config = BADGE_CONFIG[badge];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <span key={badge} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${config.color}`}>
                  <Icon size={10} />
                  {config.label}
                </span>
              );
            })}
          </div>
        )}

        {/* Services */}
        <div className="flex flex-wrap gap-1 mt-2">
          {(provider.serviceTypes || []).slice(0, 3).map(type => (
            <span key={type} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize">
              {type.replace(/_/g, ' ')}
            </span>
          ))}
        </div>

        {/* Price & Area */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <div>
            {hourlyRate ? (
              <span className="text-lg font-bold text-[#1F3A68]">£{hourlyRate}<span className="text-xs font-normal text-gray-400">/hr</span></span>
            ) : (
              <span className="text-sm text-gray-500">Custom quotes</span>
            )}
          </div>
          {provider.serviceArea?.postcodes?.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <MapPin size={12} />
              {provider.serviceArea.postcodes.slice(0, 2).join(', ')}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => navigate(`/provider/${provider.providerId}`)}
            className="flex-1 py-2 px-3 text-sm font-medium text-[#1F3A68] border border-[#1F3A68] rounded-lg hover:bg-[#1F3A68] hover:text-white transition-colors"
          >
            View Profile
          </button>
          <button
            onClick={() => onRequestQuote ? onRequestQuote(provider) : navigate(`/services/request?provider=${provider.providerId}&category=${(provider.serviceTypes || [])[0] || ''}`)}
            className="flex-1 py-2 px-3 text-sm font-medium text-white bg-[#1F3A68] rounded-lg hover:bg-[#162d52] transition-colors"
          >
            Get Quote
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderCard;
